import React, { ElementType, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  LockIcon,
  RepeatIcon,
  ViewIcon,
} from '@chakra-ui/icons';
import { FaFacebook } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getCurentOrgDataFromStorage } from '../../../services/organization';
import { Organization } from '../../../utils/types';
import { getFacebookProfile } from '../../../hooks/facebook';
import { FacebookOnboardingButton } from '../components/FacebookOnboardingButton';

const benefits = [
  { icon: CheckCircleIcon, title: 'Unified inbox for messages and comments' },
  { icon: LockIcon, title: 'Secure connection via Meta APIs' },
  { icon: ViewIcon, title: 'Track message insights and engagement' },
  { icon: RepeatIcon, title: 'Automate responses and manage leads' },
];

export const FacebookOnboarding: React.FC = () => {
  const [org, setOrg] = useState<Organization>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const fetchOrgAndCheckConnection = async () => {
      setLoading(true);
      try {
        const response = await getCurentOrgDataFromStorage();
        if (cancelled) return;

        if (
          response.success &&
          response.data &&
          typeof response.data === 'object'
        ) {
          const organization = response.data;
          setOrg(organization);

          const fbRes = await getFacebookProfile(organization.id);
          if (fbRes?.success && Array.isArray(fbRes.data) && fbRes.data.length > 0) {
            navigate(`/organization/${organization.id}/inbox/facebook/conversations`);
            return;
          }
        } else {
          throw new Error('Failed to load organization.');
        }
      } catch (err) {
        setError('Failed to load organization data.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrgAndCheckConnection();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="#f5f7fb">
        <Text color="gray.500">Preparing Facebook inbox setup</Text>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="#f5f7fb" px={{ base: 4, md: 6 }} py={{ base: 8, md: 10 }}>
      <Flex maxW="1080px" mx="auto" direction={{ base: 'column', lg: 'row' }} gap={{ base: 6, lg: 10 }}>
        <VStack align="stretch" spacing={6} flex="1" pt={{ base: 0, lg: 6 }}>
          <HStack spacing={4}>
            <Flex
              w="56px"
              h="56px"
              borderRadius="18px"
              align="center"
              justify="center"
              bg="white"
              border="1px solid"
              borderColor="blackAlpha.100"
              boxShadow="0 14px 36px rgba(15,23,32,0.06)"
            >
              <Icon as={FaFacebook as ElementType} boxSize={8} color="#1877F2" />
            </Flex>
            <VStack align="flex-start" spacing={0.5}>
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.18em" color="gray.500" fontWeight="700">
                Facebook
              </Text>
              <Text fontSize="sm" color="gray.600">
                {org?.name || 'Organization workspace'}
              </Text>
            </VStack>
          </HStack>

          <VStack align="stretch" spacing={4} maxW="560px">
            <Heading fontSize={{ base: '3xl', md: '5xl' }} lineHeight={{ base: 1.08, md: 1.02 }} letterSpacing="-0.04em" color="gray.900">
              Connect Your Facebook Page
            </Heading>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" lineHeight="1.8">
              Integrate your Facebook Page to manage messages, comments, and customer follow-up from one inbox.
            </Text>
          </VStack>

          <VStack align="stretch" spacing={3} maxW="560px">
            {benefits.map(({ icon, title }) => (
              <HStack
                key={title}
                spacing={3}
                px={4}
                py={3.5}
                bg="white"
                border="1px solid"
                borderColor="blackAlpha.100"
                borderRadius="18px"
              >
                <Icon as={icon} color="#1877F2" boxSize={5} />
                <Text fontWeight="600" color="gray.800">
                  {title}
                </Text>
              </HStack>
            ))}
          </VStack>
        </VStack>

        <Box
          flex="1"
          maxW={{ base: 'full', lg: '460px' }}
          bg="white"
          border="1px solid"
          borderColor="blackAlpha.100"
          borderRadius="28px"
          boxShadow="0 24px 60px rgba(15,23,32,0.06)"
          px={{ base: 6, md: 8 }}
          py={{ base: 7, md: 8 }}
        >
          <VStack align="stretch" spacing={5}>
            <VStack align="stretch" spacing={2}>
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.18em" color="gray.500" fontWeight="700">
                Setup
              </Text>
              <Heading fontSize={{ base: '2xl', md: '3xl' }} letterSpacing="-0.03em" color="gray.900">
                Connect in a few steps
              </Heading>
              <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                You will sign in with Meta, grant page permissions, and return here to finish the connection.
              </Text>
            </VStack>

            {error ? (
              <Alert status="error" borderRadius="16px">
                <AlertIcon />
                {error}
              </Alert>
            ) : null}

            <FacebookOnboardingButton />

            <Text fontSize="sm" color="gray.500" lineHeight="1.7">
              The first connected page from your Meta account will be linked to this organization with the current backend flow.
            </Text>
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};
